import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronsRight, Lock } from "lucide-react";

// A simple "grown-ups only" drag-to-unlock gate to prevent little kids
// from opening the Parent Area or the Upgrade screen by accident.
export default function GrownUpGate({ open, onClose, onUnlock }) {
  const trackRef = useRef(null);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (open) setUnlocked(false);
  }, [open]);

  const handleDragEnd = (_e, info) => {
    const track = trackRef.current;
    if (!track || unlocked) return;
    const available = track.offsetWidth - 64;
    if (info.offset.x >= available * 0.75) {
      setUnlocked(true);
      setTimeout(() => onUnlock(), 260);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="grownup-gate"
        >
          <motion.div
            initial={{ scale: 0.85, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 18 }}
            className="bg-white rounded-[2rem] border-4 border-slate-900/10 p-8 w-full max-w-sm text-center"
          >
            <div className="flex items-center justify-end -mt-2 -mr-2 mb-1">
              <button
                data-testid="gate-close-button"
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center"
              >
                <X size={22} strokeWidth={3} className="text-slate-500" />
              </button>
            </div>
            <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <Lock size={30} strokeWidth={3} className="text-yellow-600" />
            </div>
            <h2 className="font-fredoka text-3xl font-bold text-slate-900">Grown-ups only</h2>
            <p className="text-base font-bold text-slate-400 mt-2 mb-7">
              Slide the button all the way to continue.
            </p>

            <div
              ref={trackRef}
              className="relative h-16 rounded-full bg-slate-100 border-2 border-slate-200 flex items-center overflow-hidden"
              data-testid="gate-track"
            >
              <span className="absolute inset-0 flex items-center justify-center font-fredoka font-bold text-slate-400 text-lg select-none pointer-events-none">
                {unlocked ? "Unlocked!" : "Slide to unlock"}
              </span>
              <motion.button
                data-testid="gate-slider"
                drag="x"
                dragConstraints={trackRef}
                dragElastic={0}
                dragSnapToOrigin={!unlocked}
                onDragEnd={handleDragEnd}
                whileTap={{ scale: 1.05 }}
                className="relative z-10 ml-1 w-14 h-14 rounded-full bg-blue-500 flex items-center justify-center cursor-grab active:cursor-grabbing"
                style={{ boxShadow: "0 4px 0 0 rgba(37,99,235,1)" }}
              >
                <ChevronsRight size={28} strokeWidth={3} className="text-white" />
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
